import React, { createContext, useEffect, useMemo, useState } from 'react';
import EXPPopupJs from 'components/common/exp-popupjs';
import { generateCssColor, generateCssComponents, generateCssLang } from 'theme/generate-theme/generate-theme2';
import { css, Global, SerializedStyles } from '@emotion/react';
import { useFetcher } from 'hooks/useFetcher';
import { cryptojs } from 'helpers';
import configThemePrimary from './configThemePrimary';
import useQueryParams from 'hooks/useQueryParams';

export type ConfigThemeInfo = {
    color: any;
    components: any;
    font: any;
};

export type ServiceCardInfo = {
    serviceName: string;
    withParams?: boolean;
    redirectUrl: string;
    openExternalBrowser?: boolean;
    withPharmacyCode?: boolean;
};
export type LogoInfo = {
    alt?: string;
    en?: string;
    th?: string;
    title?: string;
};

export type ThemeConfig = {
    name: string;
    theme: ConfigThemeInfo;
    logo: LogoInfo;
    serviceCard: ServiceCardInfo[];
};

export interface ConfigThemeContextProps extends ThemeConfig {
    onChangeTheme: (token?: string | null, forcedTheme?: string | null, localTheme?: string) => Promise<void>;
}

export interface ConfigThemeProviderComponent {
    children: React.ReactNode;
}

const ThemeConfigContext = createContext<ConfigThemeContextProps>({ name: 'default' } as ConfigThemeContextProps);

const ThemeConfigConsumer = ThemeConfigContext.Consumer;

const GlobalStyle = ({ styles }: { styles: any }) => <Global styles={styles} />;

const ThemeConfigProvider = ({ children }: Readonly<ConfigThemeProviderComponent>) => {
    const { POST } = useFetcher();
    const { query } = useQueryParams();
    // const { t } = useTranslation();
    //
    const [configThemeData, setConfigThemeData] = useState<ThemeConfig>({ name: 'default' } as ThemeConfig);
    // const [configThemeData, setConfigThemeData] = useState<ThemeConfig>({
    //     logo: configThemePrimary.logo,
    //     serviceCard: configThemePrimary.serviceCard,
    //     theme: configThemePrimary.theme,
    //     name: configThemePrimary.name,
    // });
    const [themeCss, setThemeCss] = useState<SerializedStyles>();
    //
    const generateThemeCss = (tName: string, tConfig: ConfigThemeInfo) => {
        const _configThemeColor = generateCssColor(tConfig.color);
        const _configThemeFont = generateCssLang(tConfig.font);
        const _configThemeComponents = generateCssComponents(tConfig.components);
        const _theme = css`
            html[theme=${`"${tName}"`}] {
                ${_configThemeColor}
                ${_configThemeFont}
                ${_configThemeComponents}
            }
        `;

        return _theme;
    };

    const getThemeConfigWithApi = (token: string | null = null, forcedTheme?: string | null) => {
        return new Promise<ThemeConfig>(async (resolve) => {
            try {
                const res = await POST('/misc/ui-theme-config', {
                    token: token,
                    forcedTheme: forcedTheme,
                });
                resolve(res.theme);
            } catch (error: any) {
                console.error('/misc/ui-theme-config error', error);
                resolve(error?.theme || configThemePrimary);

                if (error.statusCode === 404) {
                    EXPPopupJs.simple('warning', 'แจ้งเตือน', 'ไม่พบ config theme');
                }
            }
        });
    };

    const onChangeTheme = async (token: string | null = null, forcedTheme?: string | null, localTheme?: string) => {
        // setConfigThemeData((prev) => ({ ...prev, logo: {}, serviceCard: [] }));

        if (token || forcedTheme) {
            const tConfig = await getThemeConfigWithApi(token, forcedTheme);
            setConfigThemeData({ ...tConfig });
        } else {
            setConfigThemeData((prev) => ({ ...prev, ...configThemePrimary }));
        }
    };

    useEffect(() => {
        const init = () => {
            let _forcedTheme = '';

            if (query?.to && cryptojs.decode(query?.to)?.data?.theme) {
                _forcedTheme = cryptojs.decode(query?.to).data.theme;
            } else if (window.location.origin.includes('fastcare.co')) {
                _forcedTheme = 'fastcare';
            } else if (window.location.origin.includes('yaphrom')) {
                _forcedTheme = 'yaphrom';
            }
            if (_forcedTheme) {
                onChangeTheme(null, _forcedTheme);
            }
        };

        init();
    }, []);

    const values: ConfigThemeContextProps = useMemo(() => {
        if (configThemeData.theme) {
            const theme = generateThemeCss(configThemeData.name, configThemeData.theme);
            setThemeCss(theme);
        }

        const HTMLElement = document.querySelector('html');
        HTMLElement?.setAttribute('theme', configThemeData.name);
        return {
            ...configThemeData,
            onChangeTheme: onChangeTheme,
        };
    }, [JSON.stringify(configThemeData)]);

    //
    return (
        <ThemeConfigContext.Provider value={values}>
            <GlobalStyle styles={themeCss} />
            {children}
        </ThemeConfigContext.Provider>
    );
};

export { ThemeConfigConsumer, ThemeConfigContext, GlobalStyle };
export default ThemeConfigProvider;
